import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const STORAGE_KEY = 'bureaugeek:cookie-consent';
const CLOSE_DURATION = 300;

/**
 * @typedef {object} Refs
 * @property {HTMLElement} [settingsPanel]
 * @property {HTMLButtonElement} [settingsToggle]
 * @property {HTMLInputElement} [analyticsToggle]
 *
 * @extends {Component<Refs>}
 */
export class CookieBanner extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#readConsent()) return;

    // Double rAF: let the initial (hidden) state paint first so the
    // enter transition has something to animate from.
    requestAnimationFrame(() => {
      this.hidden = false;

      requestAnimationFrame(() => {
        this.classList.add('cookie-banner--visible');
      });
    });
  }

  acceptAll() {
    this.#saveConsent({ status: 'accepted', necessary: true, analytics: true });
    this.#dismiss();
  }

  rejectAll() {
    this.#saveConsent({ status: 'rejected', necessary: true, analytics: false });
    this.#dismiss();
  }

  savePreferences() {
    const analytics = Boolean(this.refs.analyticsToggle?.checked);
    this.#saveConsent({ status: 'custom', necessary: true, analytics });
    this.#dismiss();
  }

  toggleSettings() {
    const panel = this.refs.settingsPanel;
    if (!panel) return;

    const willOpen = panel.hidden;
    panel.hidden = !willOpen;
    this.refs.settingsToggle?.setAttribute('aria-expanded', String(willOpen));
  }

  #dismiss() {
    this.classList.remove('cookie-banner--visible');
    this.classList.add('cookie-banner--closing');

    setTimeout(
      () => {
        this.hidden = true;
        this.classList.remove('cookie-banner--closing');
      },
      prefersReducedMotion() ? 0 : CLOSE_DURATION
    );
  }

  /**
   * @param {{status: string, necessary: boolean, analytics: boolean}} consent
   */
  #saveConsent(consent) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...consent, timestamp: new Date().toISOString() }));
    } catch {
      // localStorage unavailable (e.g. private browsing): the banner simply reappears next visit.
    }

    this.dispatchEvent(new CustomEvent('cookie-consent:updated', { bubbles: true, detail: consent }));
  }

  #readConsent() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
}

if (!customElements.get('cookie-banner-component')) {
  customElements.define('cookie-banner-component', CookieBanner);
}
